import React, { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { Compass, Lock } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { formatApiError } from "@/lib/api";

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
    <path fill="#4285F4" d="M23.5 12.3c0-.9-.1-1.5-.3-2.2H12v4h6.5c-.1 1-.8 2.6-2.4 3.6l3.7 2.9c2.2-2 3.7-5 3.7-8.3z" />
    <path fill="#34A853" d="M12 24c3.2 0 5.9-1.1 7.9-2.9l-3.7-2.9c-1 .7-2.4 1.2-4.2 1.2-3.2 0-5.9-2.1-6.8-5H1.4v3C3.4 21.3 7.4 24 12 24z" />
    <path fill="#FBBC05" d="M5.2 14.4c-.2-.7-.4-1.5-.4-2.4s.2-1.7.4-2.4v-3H1.4C.5 8.2 0 10 0 12s.5 3.8 1.4 5.4l3.8-3z" />
    <path fill="#EA4335" d="M12 4.8c2.3 0 3.8 1 4.7 1.8L20 3.3C18.1 1.5 15.6 0 12 0 7.4 0 3.4 2.7 1.4 6.6l3.8 3c.9-2.9 3.6-4.8 6.8-4.8z" />
  </svg>
);

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(location.state?.googleError || "");

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-obsidian text-cream flex flex-col items-center justify-center p-5" data-testid="admin-login-page">
      <Link to="/" className="flex items-center gap-2.5 mb-10" data-testid="login-logo">
        <Compass className="h-6 w-6 text-copper" />
        <span className="font-serif text-xl tracking-[0.18em] uppercase">De Los Cielos</span>
      </Link>
      <form onSubmit={submit} className="w-full max-w-sm card-dark p-8" data-testid="admin-login-form">
        <div className="flex items-center gap-3 mb-8">
          <Lock className="h-5 w-5 text-copper" />
          <div>
            <h1 className="font-serif text-2xl">Area Riservata</h1>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-sand mt-0.5">Admin & collaboratori</p>
          </div>
        </div>
        <div className="space-y-4">
          <div>
            <label className="label" htmlFor="a-email">Email</label>
            <input id="a-email" type="email" required className="field" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-login-email" />
          </div>
          <div>
            <label className="label" htmlFor="a-password">Password</label>
            <input id="a-password" type="password" required className="field" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-login-password" />
          </div>
        </div>
        {error && <p className="mt-4 text-sm text-red-400" data-testid="admin-login-error">{error}</p>}
        <button type="submit" disabled={busy} className="btn-copper w-full mt-8 disabled:opacity-50" data-testid="admin-login-submit">
          {busy ? "Accesso…" : "Entra"}
        </button>
        <div className="flex items-center gap-3 my-6">
          <span className="h-px flex-1 bg-white/10" />
          <span className="font-mono text-[10px] uppercase tracking-widest text-sand">oppure</span>
          <span className="h-px flex-1 bg-white/10" />
        </div>
        <button type="button" onClick={googleLogin} className="btn-ghost w-full" data-testid="google-login-btn">
          <GoogleIcon /> Continua con Google
        </button>
        <div className="mt-5 text-center">
          <Link to="/admin/password-dimenticata" className="font-mono text-[10px] uppercase tracking-widest text-sand hover:text-copperlight transition-colors" data-testid="forgot-password-link">
            Password dimenticata?
          </Link>
        </div>
        <p className="mt-4 font-mono text-[10px] text-sand text-center">Accesso riservato a staff autorizzato · solo email registrate</p>
      </form>
    </div>
  );
}
